import { Component, computed, OnInit, signal } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { AfasFunction } from '../classes/afas-function.class';
import { Role } from '../classes/auth/role.class';
import { CodaltComponent } from '../codalt.component';
import { AfasFunctionService } from '../services/afas-function.service';
import { AuthorisationService } from '../services/auth/authorisation.service';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';

@Component({
    selector: 'app-afas-functions-management',
    standalone: false,
    templateUrl: './afas-functions-management.component.html',
    styleUrl: './afas-functions-management.component.scss',
})
export class AfasFunctionsManagementComponent extends CodaltComponent implements OnInit {
    functions = signal<AfasFunction[] | null>(null);
    roles = signal<Role[] | null>(null);
    search = signal<string>('');
    saving = signal<boolean>(false);
    isProduction = environment.production;

    filteredFunctions = computed(() => {
        const searchValue = this.search().toLowerCase();
        const allFunctions = this.functions() || [];

        if (!searchValue) {
            return allFunctions;
        }

        return allFunctions.filter(
            f => f.name.toLowerCase().includes(searchValue) || f.key.toLowerCase().includes(searchValue)
        );
    });

    constructor(
        private afasFunctionService: AfasFunctionService,
        private authorisationService: AuthorisationService,
        private confirmDialogService: ConfirmDialogService,
        private router: Router
    ) {
        super();
    }

    ngOnInit() {
        this.loadData();
    }

    loadData() {
        this.subscriptions.add(
            this.afasFunctionService.getList().subscribe(functions => {
                if (!functions?.length) {
                    this.confirmDialogService.confirm({
                        title: 'Geen functies',
                        text: 'Er zijn geen AFAS functies beschikbaar. Voer Afas synchronisatie uit.',
                    });
                }
                this.functions.set(functions);
            })
        );

        this.subscriptions.add(
            this.authorisationService.getRoles().subscribe(roles => {
                this.roles.set(
                    roles.sort((a, b) => {
                        if (a.is_unique !== b.is_unique) {
                            return a.is_unique ? -1 : 1;
                        }

                        return a.name.localeCompare(b.name);
                    })
                );
            })
        );
    }

    onSearchChange(value: string) {
        this.search.set(value);
    }

    navigateToUsers(funcName: string) {
        this.router.navigate(['/gebruikers'], { queryParams: { search: funcName } });
    }

    private updateFunctionRoles(functionKey: string, roles: Role[]) {
        const allFunctions = this.functions();

        if (!allFunctions?.length) {
            return;
        }

        this.functions.set(
            allFunctions.map(currentFunction =>
                currentFunction.key === functionKey ? { ...currentFunction, roles } : currentFunction
            )
        );
    }

    toggleRole(event: MatCheckboxChange, func: AfasFunction, role: Role) {
        const checked = event.checked;
        const previousRoles = func.roles || [];
        const currentRoleKeys = previousRoles.map(r => r.key);
        let newRoleKeys: string[];

        if (checked) {
            newRoleKeys = [...currentRoleKeys, role.key];
        } else {
            newRoleKeys = currentRoleKeys.filter(key => key !== role.key);
        }

        const selectedRoles = (this.roles() || []).filter(r => newRoleKeys.includes(r.key));

        // Optimistic update so disabled states refresh immediately.
        this.updateFunctionRoles(func.key, selectedRoles);

        this.saving.set(true);
        this.subscriptions.add(
            this.afasFunctionService.saveRoles(func.key, newRoleKeys).subscribe({
                next: () => {
                    this.saving.set(false);
                },
                error: _error => {
                    this.updateFunctionRoles(func.key, previousRoles);
                    this.saving.set(false);
                    event.source.checked = !checked;
                },
            })
        );
    }
}
